import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import BottomNav from "../components/BottomNav";
import { EmptyRideVisual, ZunoVehicle } from "../components/ZunoVisuals";
import { getHistoricoCorridas } from "../services/rideService";
import { gerarReciboPDF } from "../services/reciboService";
import activityHeroAsset from "../assets/zuno/activity-empty.webp";
import zunoMark from "../assets/zuno-splash.png";
import { LuFileDown, LuRefreshCw, LuX } from "react-icons/lu";

const PERIODOS = [
  ["semana", "7 dias"],
  ["mes", "Este mês"],
  ["tudo", "Tudo"],
];

export function rideDate(ride) {
  const raw = ride?.concluidoEm || ride?.criadoEm;
  if (!raw) return null;
  const date = raw.toDate ? raw.toDate() : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function ridePrice(ride) {
  const value = ride?.valorFinalComTaxa ?? ride?.valor ?? ride?.finalPrice ?? ride?.dadosCorrida?.finalPrice ?? 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function filterCompletedRides(rides, period, now = new Date()) {
  return rides
    .filter((ride) => ride.status === "completed")
    .filter((ride) => {
      const date = rideDate(ride);
      if (!date) return period === "tudo";
      if (period === "semana") {
        const elapsed = now.getTime() - date.getTime();
        return elapsed >= 0 && elapsed <= 7 * 24 * 60 * 60 * 1000;
      }
      if (period === "mes") {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }
      return true;
    })
    .sort((a, b) => (rideDate(b)?.getTime() || 0) - (rideDate(a)?.getTime() || 0));
}

function formatCurrency(value) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function formatDate(value) {
  const date = rideDate(value);
  return date ? date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : "Data indisponível";
}

function destinationLabel(ride) {
  if (typeof ride?.destino === "string") return ride.destino;
  return ride?.destino?.nome || ride?.destino?.endereco || ride?.destinationAddress || "Destino";
}

function categoryInfo(category) {
  if (category === "moto") return { type: "moto", label: "Zuno Moto" };
  if (category === "elite" || category === "comfort") return { type: "elite", label: "Zuno Elite" };
  return { type: "go", label: "Zuno Go" };
}

function Activity() {
  const navigate = useNavigate();
  const [corridas, setCorridas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [corridaSelecionada, setCorridaSelecionada] = useState(null);
  const [periodoFiltro, setPeriodoFiltro] = useState("mes");

  const loadHistory = useCallback(async (user) => {
    setLoading(true);
    setError("");
    try {
      const result = await getHistoricoCorridas(user.uid, "passenger", { pageSize: 50 });
      if (result.error) throw result.error;
      const history = result.corridas;
      setCorridas(history);
    } catch (loadError) {
      console.error("Erro ao carregar atividade:", loadError);
      setError("Não foi possível carregar suas corridas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      loadHistory(user);
    });
    return unsubscribe;
  }, [loadHistory, retryKey]);

  const completedRides = useMemo(() => filterCompletedRides(corridas, "tudo"), [corridas]);
  const filteredRides = useMemo(() => filterCompletedRides(corridas, periodoFiltro), [corridas, periodoFiltro]);
  const totalSpent = useMemo(() => filteredRides.reduce((total, ride) => total + ridePrice(ride), 0), [filteredRides]);
  const hasTrips = completedRides.length > 0;

  return (
    <main className={`zuno-app-shell activity-screen ${hasTrips ? "activity-screen--with-rides" : ""}`}>
      {hasTrips ? (
        <section className="activity-premium-hero" aria-labelledby="activity-title">
          <img className="activity-premium-art" src={activityHeroAsset} alt="" aria-hidden="true" />
          <div className="activity-premium-fade" aria-hidden="true" />
          <div className="activity-premium-heading">
            <div className="activity-brand"><img src={zunoMark} alt="" /><span>ZUNO</span></div>
            <h1 id="activity-title">Atividade</h1>
          </div>
        </section>
      ) : (
        <header className="zuno-page-header activity-empty-header">
          <img src={zunoMark} alt="" aria-hidden="true" />
          <h1>Atividade</h1>
        </header>
      )}

      {!loading && hasTrips && (
        <div className="activity-content">
          <div className="activity-periods" aria-label="Período das corridas">
            {PERIODOS.map(([value, label]) => (
              <button key={value} type="button" data-active={periodoFiltro === value} onClick={() => setPeriodoFiltro(value)}>
                {label}
              </button>
            ))}
          </div>

          <section className="activity-summary" aria-live="polite">
            <h2>Resumo financeiro</h2>
            <div className="activity-summary-grid">
              <div><span>Total gasto</span><strong>{formatCurrency(totalSpent)}</strong></div>
              <div><span>Corridas</span><strong>{filteredRides.length}</strong></div>
            </div>
          </section>

          <section className="activity-rides">
            <h2>Corridas recentes</h2>
            {filteredRides.length > 0 ? (
              <div className="activity-rides-list" key={periodoFiltro}>
                {filteredRides.map((ride, index) => {
                  const category = categoryInfo(ride.categoria);
                  return (
                    <button
                      className="activity-ride-card"
                      type="button"
                      key={ride.id || index}
                      onClick={() => setCorridaSelecionada(ride)}
                      style={{ "--ride-index": index }}
                    >
                      <span className="activity-ride-vehicle"><ZunoVehicle type={category.type} /></span>
                      <span className="activity-ride-copy">
                        <strong>{destinationLabel(ride)}</strong>
                        <small>• {category.label} · {formatDate(ride)}</small>
                      </span>
                      <strong className="activity-ride-price">{formatCurrency(ridePrice(ride))}</strong>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="activity-period-empty">
                <p>Nenhuma corrida concluída neste período.</p>
                <button type="button" onClick={() => setPeriodoFiltro("tudo")}>Ver todo o histórico</button>
              </div>
            )}
          </section>
        </div>
      )}

      {loading && (
        <div className="activity-loading" aria-label="Carregando atividade">
          <div className="activity-skeleton activity-skeleton--hero" />
          <div className="activity-skeleton activity-skeleton--filters" />
          <div className="activity-skeleton activity-skeleton--card" />
        </div>
      )}

      {!loading && error && (
        <div className="activity-error">
          <p>{error}</p>
          <button type="button" onClick={() => setRetryKey((value) => value + 1)}><LuRefreshCw /> Tentar novamente</button>
        </div>
      )}

      {!loading && !error && !hasTrips && (
        <section className="activity-empty-state">
          <div className="zuno-activity-hero"><EmptyRideVisual /></div>
          <h2>Nenhuma corrida ainda</h2>
          <p>Peça sua primeira corrida e ela aparecerá aqui.</p>
          <button type="button" onClick={() => navigate("/passenger-home")}>Pedir corrida agora</button>
        </section>
      )}

      {corridaSelecionada && (
        <div className="activity-detail-overlay" onClick={() => setCorridaSelecionada(null)}>
          <section className="activity-detail-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="zuno-sheet-handle" />
            <button className="activity-detail-close" type="button" aria-label="Fechar" onClick={() => setCorridaSelecionada(null)}><LuX /></button>
            <span className="activity-detail-vehicle"><ZunoVehicle type={categoryInfo(corridaSelecionada.categoria).type} /></span>
            <h2>{destinationLabel(corridaSelecionada)}</h2>
            <p>{formatDate(corridaSelecionada)} · {categoryInfo(corridaSelecionada.categoria).label}</p>
            <div className="activity-detail-route">
              <span>Embarque<strong>{corridaSelecionada.origem?.endereco || "Localização de embarque"}</strong></span>
              <span>Destino<strong>{destinationLabel(corridaSelecionada)}</strong></span>
            </div>
            <div className="activity-detail-price"><span>Valor final</span><strong>{formatCurrency(ridePrice(corridaSelecionada))}</strong></div>
            <button className="activity-receipt-button" type="button" onClick={() => gerarReciboPDF(corridaSelecionada)}><LuFileDown /> Baixar recibo PDF</button>
          </section>
        </div>
      )}

      <BottomNav active="atividade" />
    </main>
  );
}

export default Activity;
